import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Apollo } from 'apollo-angular';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  loginWithPassword,
  logout,
  resetPassword,
  currentUser,
  currentUserNoVenue,
  ssoLoginOptions,
  completeHostedLogin,
} from '../gql/consts';
import { LocalStoreService } from './localstore.service';
import { User } from '../models/models';
import { Store } from '@ngxs/store';
import { StateReset } from 'ngxs-reset-plugin';
import { ZoneAction } from '../store/zones/zones.actions';
import {
  authRefusalMessage,
  defaultCallbackUrl,
  hostedLoginRedirect,
  withRedirectUri,
} from '../utils/hostedLoginClient';

export interface SsoLoginOptions {
  ssoAvailable: boolean;
  ssoRequired: boolean;
  providerName: string | null;
  providerKind: string | null;
  startUrl: string | null;
}

/** A refusal worth showing as-is (disabled account, SSO/MFA problem) rather than "incorrect password". */
export class UserFacingAuthError extends Error {
  readonly userFacing = true;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private user: User;
  public onUserChanged: BehaviorSubject<User> = new BehaviorSubject(null);

  constructor(
    private localstoreService: LocalStoreService,
    private apollo: Apollo,
    private router: Router,
    private store: Store
  ) { }

  public isAuthenticated(): boolean {
    const token = this.localstoreService.getItemParsed('access_token');

    return (token != null && token.loginToken != null);
  }

  /**
   * Resolves `'redirecting'` when the sign-in continues on api-v2's hosted SSO/MFA pages.
   * Rejects with a `UserFacingAuthError` for a refusal worth showing as-is.
   */
  login(email: string, password: string) {
    return new Promise<true | 'redirecting'>((resolve, reject) => {
      this.apollo.mutate({
        mutation: loginWithPassword,
        variables: { email, password }
      })
        .subscribe(
          (token: any) => {
            if (token.data  && token.data.loginWithPassword) {
              this.startSession(token.data.loginWithPassword.loginToken)
                .then(() => resolve(true))
                .catch(() => reject());
            } else {
              reject();
            }
          },
          (error) => {
            console.log(error);
            const hosted = hostedLoginRedirect(error, defaultCallbackUrl(window));
            if (hosted && hosted.url) {
              window.location.assign(hosted.url);
              resolve('redirecting');
              return;
            }
            const message = (hosted && hosted.message) || authRefusalMessage(error);
            reject(message ? new UserFacingAuthError(message) : undefined);
          }
        );
    });
  }

  ssoLoginOptions(email: string): Observable<SsoLoginOptions | null> {
    return this.apollo.query({
      query: ssoLoginOptions,
      variables: { email },
      fetchPolicy: 'no-cache'
    })
      .pipe(
        map((result: any) => (result && result.data && result.data.ssoLoginOptions) || null),
        catchError(() => of(null))
      );
  }

  ssoStartUrl(startUrl: string | null): string | null {
    return withRedirectUri(startUrl, defaultCallbackUrl(window));
  }

  /** Exchanges the one-time #audCode the hosted pages returned for a session. */
  completeHostedLogin(handoffCode: string) {
    return new Promise<void>((resolve, reject) => {
      this.apollo.mutate({
        mutation: completeHostedLogin,
        variables: { handoffCode }
      })
        .subscribe(
          (result: any) => {
            const session = result && result.data && result.data.completeHostedLogin;
            if (!session || !session.loginToken) {
              reject(new UserFacingAuthError('This sign-in link has expired or was already used. Start again.'));
              return;
            }
            this.startSession(session.loginToken).then(resolve).catch(reject);
          },
          (error) => {
            console.log(error);
            reject(error);
          }
        );
    });
  }

  private startSession(loginToken: string) {
    this.localstoreService.setItem('access_token', JSON.stringify({ loginToken }));
    this.localstoreService.removeItem('venueId');
    this.localstoreService.removeItem('venue.id');
    return this.getUser().catch((err) => {
      console.log(err);
      throw err;
    });
  }

  logout() {
    return new Promise<void>((resolve, reject) => {
      this.apollo.mutate({
        mutation: logout
      })
        .subscribe(
          (data) => {
            if (data) {
              this.localstoreService.clear();
              this.store.dispatch(new ZoneAction.ClearZoneData());
              this.store.dispatch(new StateReset());

              this.user = null;
              this.onUserChanged.next(this.user);

              this.restoreAuthorization();
            }

            resolve();
          },
          (error) => {
            console.log(error);
            reject(error);
          }
        );
    });
  }

  resetPassword(email: string) {
    return this.apollo.mutate({
      mutation: resetPassword,
      variables: { email }
    });
  }

  getUserWithVenue() {
    return new Promise<any>((resolve, reject) => {
      this.apollo.query({
        query: currentUser,
        variables: { venueId: this.localstoreService.getItemParsed('venue.id') }
      })
        .subscribe(
          (data: any) => {
            if (data && data.data && data.data.me) {
              this.user = data.data.me;
              this.onUserChanged.next(this.user);
            }
            if (data.errors && data.errors.length > 0) {
              const isServerError = data.errors.some(
                err => err.extensions && err.extensions.code === 'INTERNAL_SERVER_ERROR'
              );
              if (isServerError) {
                this.localstoreService.removeItem('access_token');
                this.router.navigate(['login']);
              }
              reject();
            }
            resolve(this.user);
          },
          (error) => {
            console.log(error);
            reject(error);
          }
        );
    });
  }

  getUser() {
    return new Promise<void>((resolve, reject) => {
      this.apollo.query({
        query: currentUserNoVenue
      })
        .subscribe(
          (data: any) => {
            if (data && data.data && data.data.me) {
              this.user = data.data.me;
              this.onUserChanged.next(this.user);
            }

            if (data.errors && data.errors.length > 0) {
              const isServerError = data.errors.some(err => err.extensions && err.extensions.code === 'INTERNAL_SERVER_ERROR');

              if (isServerError) {
                this.localstoreService.removeItem('access_token');
                this.router.navigate(['login']);
              }

              reject();
            }

            resolve();
          },
          (error) => {
            console.log(error);
            reject(error);
          }
      );
    });
  }

  private restoreAuthorization() {
    // reset the store
    this.apollo.getClient().resetStore();
  }
}
